import proj4 from "proj4";
import * as mgrs from "mgrs";

import { CoordinatesAlias } from "constant";

const DMS_REGEXP = /(\d+)°(\d+)'([\d.]+)"([NSEW])/;
const MGRS_REGEXP = /^[0-9]{1,2}[C-HJ-NP-X][A-Z]{2}\d{0,10}$/i;
const UTM_REGEXP = /^\d{1,2} \d+(\.\d+)? \d+(\.\d+)?$/;

// UTM zone by longitude
function getUTMZone(lon: number): number {
  return Math.floor((lon + 180) / 6) + 1;
}

// DMS <-> DD
function ddToDms(dd: number, isLat: boolean): string {
  const dir = dd >= 0 ? (isLat ? "N" : "E") : isLat ? "S" : "W";
  const abs = Math.abs(dd);
  const deg = Math.floor(abs);
  const min = Math.floor((abs - deg) * 60);
  const sec = ((abs - deg - min / 60) * 3600).toFixed(2);
  return `${deg}°${min}'${sec}"${dir}`;
}

function dmsToDd(dms: string): number {
  const match = dms.match(DMS_REGEXP);
  if (!match) throw new Error("Invalid DMS format");
  const [, d, m, s, dir] = match;
  let dd = +d + +m / 60 + +s / 3600;
  if (dir === "S" || dir === "W") dd *= -1;
  return dd;
}

// Automatic type detection
function detectCoordType(input: string | [number, number]): CoordinatesAlias {
  if (Array.isArray(input)) return CoordinatesAlias.DD;

  const str = input.trim();

  if (MGRS_REGEXP.test(str)) return CoordinatesAlias.MGRS;
  if (UTM_REGEXP.test(str)) return CoordinatesAlias.UTM;
  if (/°.*'.*".*[NSEW]/.test(str)) return CoordinatesAlias.DMS;

  throw new Error("Unknown coordinate format");
}

// Basic utility
export function convertCoordinates(
  input: string | [number, number],
  to: CoordinatesAlias,
  from?: CoordinatesAlias
): string {
  const source = from ?? detectCoordType(input);
  let dd: [number, number];

  // to WGS84 DD
  switch (source) {
    case CoordinatesAlias.DD:
      dd = input as [number, number];
      break;
    case CoordinatesAlias.DMS: {
      const [latStr, lonStr] = (input as string)
        .split(",")
        .map((s) => s.trim());
      dd = [dmsToDd(lonStr), dmsToDd(latStr)];
      break;
    }
    case CoordinatesAlias.UTM: {
      const [zoneStr, eastingStr, northingStr] = (input as string).split(" ");
      const zone = parseInt(zoneStr);
      const utmProj = `+proj=utm +zone=${zone} +datum=WGS84 +units=m +no_defs`;
      dd = proj4(utmProj, "EPSG:4326").forward([+eastingStr, +northingStr]) as [
        number,
        number
      ];
      break;
    }
    case CoordinatesAlias.MGRS:
      dd = mgrs.toPoint(input as string) as [number, number];
      break;
  }

  // from WGS84 DD to target
  switch (to) {
    case CoordinatesAlias.DD:
      return `${dd[0]}, ${dd[1]}`;
    case CoordinatesAlias.DMS:
      return `${ddToDms(dd[0], true)}, ${ddToDms(dd[1], false)}`;
    case CoordinatesAlias.UTM: {
      const zone = getUTMZone(dd[1]);
      const isSouthern = dd[0] < 0;
      const utmProj = `+proj=utm +zone=${zone} ${
        isSouthern ? "+south" : ""
      } +datum=WGS84 +units=m +no_defs`;
      const [e, n] = proj4("EPSG:4326", utmProj).forward([dd[1], dd[0]]);
      return `${zone} ${e.toFixed(2)} ${n.toFixed(2)}`;
    }
    case CoordinatesAlias.MGRS:
      return mgrs.forward([dd[1], dd[0]]);
  }
}
